/**
 * LeftRail — columna de TRABAJO (GEN-PT-006): todo lo de la conversación.
 *
 * Orden: Agentes en este chat · Agentes por departamento (plegados salvo el
 * primero) · Ficheros · Nueva conversación · Conversaciones.
 * La gestión (Mi equipo, Administración…) vive en los botones del raíl (GEN-PX-021 retiró el panel derecho).
 * GEN-PX-023: si el botón del raíl activo tiene hijos (`seccion`), el carril pasa a ser contextual y lista esas entradas
 * (CarrilContextual) en lugar de lo anterior; sin hijos, sigue siendo este de siempre. Se pliega con el mismo control.
 *
 * Renderiza lo que recibe del descriptor:
 *  - Historial de chats (descriptor.chats)
 *  - Agentes (descriptor.agents_visibles), por departamento
 *  - Ficheros (descriptor.capabilities.menu, id `ficheros`) — data-driven
 *
 * R-PT-02: data-driven. No asume secciones — pinta capabilities.menu.
 * R-PT-06: lista de chats con "de qué va" y agentes implicados.
 */

import { useState } from 'react'
import { MessageSquare, Users, FileText, FolderOpen, Bookmark, ChevronDown, ChevronRight, Trash2 } from 'lucide-react'
import { useSession } from '../context/SessionContext.jsx'
import { AgentAvatar } from './AgentAvatar.jsx'
import { agentColorOf } from '../lib/agent-colors.js'
import { useFotoAgente } from '../context/FotosAgentes.jsx'
import { groupByDepartment } from '../lib/agent-groups.js'
import { AgentesEnEsteChat } from './AgentesEnEsteChat.jsx'
import { PREFIJO_MECANISMO } from '../mecanismos/registro.js'
import { PREFIJO_EMPRESA } from '../lib/menu.js'
import CarrilContextual from './CarrilContextual.jsx'

// #459: iconografía de línea (lucide) en vez de emojis Unicode; heredan currentColor.
const SECTION_ICONS = { reports: FileText, areas: FolderOpen, files: FolderOpen }
const FALLBACK_ICON = Bookmark
// Ítems del menú que NO son de la columna de trabajo (viven en RightRail) o que
// tienen cuerpo propio aquí. El resto (Informes, Áreas…) se pinta tras Ficheros.
// GEN-PX-018: «estrategia» es solo del raíl; no se repite como enlace en este panel.
const IDS_PROPIOS = ['chats', 'agentes', 'personas', 'estrategia', 'saber', 'ficheros', 'equipo', 'admin', 'detenciones', 'auditoria', 'coste', 'agentes-permisos', 'herramientas', 'modelos', 'salud', 'libro-de-estilo']
const NAV_ICON_PROPS = { size: 17, strokeWidth: 1.8, 'aria-hidden': true, className: 'rail-section__ico' }

function Chevron({ open }) {
  const Ico = open ? ChevronDown : ChevronRight
  return <Ico size={16} strokeWidth={1.9} aria-hidden="true" />
}

function formatRelativeTime(isoString) {
  const t = new Date(isoString).getTime()
  if (Number.isNaN(t)) return ''            // #277: caché sin updated_at
  const diff  = Date.now() - t
  const mins  = Math.floor(diff / 60_000)
  const hours = Math.floor(diff / 3_600_000)
  const days  = Math.floor(diff / 86_400_000)
  if (mins < 60)  return `hace ${mins}m`
  if (hours < 24) return `hace ${hours}h`
  return `hace ${days}d`
}

function ChatItem({ chat, active, onClick, onDelete }) {
  // #277: un chat del descriptor puede llegar parcial (US-62: la lista viene
  // del bootstrap y un chat puede no traer `de_que_va`/`agentes_en_chat`).
  // Guardamos los accesos para que pintar un chat incompleto no rompa el rail.
  const deQueVa = chat.de_que_va ?? ''
  // GEN-195: la lista muestra quien respondió el último turno (`agente_ultimo`,
  // como el historial); un kernel anterior no lo manda -> lo que hubiera.
  const agentes = chat.agente_ultimo?.nombre
    ?? (chat.agentes_en_chat ?? []).map(a => a.nombre).join(', ')
  const titulo  = chat.titulo ?? '(sin título)'
  return (
    // US-467: fila con el botón de selección + el de borrar como HERMANOS (nada
    // de <button> anidados, HTML inválido). El fondo hover/activo vive en la
    // fila para que no parpadee al pasar sobre el icono de borrar.
    <div className={'chat-item-row' + (active ? ' chat-item-row--active' : '')}>
      <button
        className={'chat-item' + (active ? ' chat-item--active' : '')}
        onClick={onClick}
        title={deQueVa}
      >
        <span className="chat-item__title">{titulo}</span>
        <span className="chat-item__meta">
          {agentes}{agentes && ' · '}{formatRelativeTime(chat.updated_at)}
        </span>
        <span className="chat-item__desc">{deQueVa}</span>
      </button>
      {onDelete && (
        <button
          type="button"
          className="chat-item__delete"
          aria-label={`Eliminar conversación ${titulo}`}
          title="Eliminar conversación"
          // Detener la propagación: borrar no debe seleccionar/abrir el chat.
          onClick={e => { e.stopPropagation(); onDelete(chat) }}
        >
          <Trash2 size={15} strokeWidth={1.8} aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

// GEN-PT-006: el agente se elige por su miniatura. Un agente inactivo sigue
// visible pero marcado como no disponible y sin acción.
function AgentThumb({ agent, ringColor, onClick }) {
  const fotoDe = useFotoAgente()  // GEN-AG-006: foto en vigor de la ficha
  const disponible = agent.puede_iniciar_chat !== false
  const titulo = disponible
    ? [agent.nombre, agent.rol].filter(Boolean).join(' · ')
    : `${agent.nombre} · No disponible`
  return (
    <button
      type="button"
      className={'agent-thumb' + (disponible ? '' : ' agent-thumb--disabled')}
      onClick={disponible ? onClick : undefined}
      disabled={!disponible}
      title={titulo}
      aria-label={disponible ? `Hablar con ${agent.nombre}` : `${agent.nombre} (no disponible)`}
    >
      <AgentAvatar name={agent.nombre} avatarUrl={fotoDe(agent)} size={40} ringColor={ringColor} />
      <span className="agent-thumb__name">{agent.nombre}</span>
    </button>
  )
}

function DepartamentoAgentes({ grupo, abierto, onToggle, ringColorOf, onSelectAgent }) {
  const activos = grupo.agents.filter(a => a.puede_iniciar_chat !== false).length
  return (
    <div className="rail-mostrador__group">
      <button
        type="button"
        className="rail-mostrador__dept rail-mostrador__dept--toggle"
        onClick={onToggle}
        aria-expanded={abierto}
      >
        <Chevron open={abierto} />
        <span className="rail-mostrador__dept-name">{grupo.departamento}</span>
        <span className="rail-section__count">{activos}/{grupo.agents.length}</span>
      </button>
      {abierto && (
        <div className="agent-thumbs">
          {grupo.agents.map(a => (
            <AgentThumb
              key={a.id ?? a.nombre}
              agent={a}
              ringColor={ringColorOf(a)}
              onClick={() => onSelectAgent(a)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function LeftRail({
  collapsed,
  activeChatId,
  activeView = 'chat',
  activeChat = null,
  sources = [],
  agentesQueRespondieron = [],
  onSelectChat,
  onNewChat,
  onSelectAgent,
  onAnadirAgente,
  onNavigate,
  onDeleteChat,
  soloHistorico = false,
  seccion = null,
  onElegirHijo,
}) {
  const { chats, agents, capabilities, agentPalette, resolvedTheme, user } = useSession()
  const [expandedSection, setExpandedSection] = useState('chats')
  // GEN-PT-006: departamentos plegados salvo el primero (Dirección en DKtec, el
  // que abre el descriptor). Estado local; recordarlo entre sesiones es fase posterior.
  const [deptAbierto, setDeptAbierto] = useState({})
  const [search, setSearch] = useState('')

  const menu = capabilities?.menu ?? []

  // Las secciones nativas (chats / agentes) tienen cuerpo propio (buscador,
  // miniaturas), pero su ETIQUETA también es data-driven (R-PT-02): se toma del
  // ítem del descriptor por id, con un default si el tenant no lo trae.
  const menuById   = Object.fromEntries(menu.map(m => [m.id, m]))
  const chatsLabel  = menuById.chats?.label   ?? 'Conversaciones'
  const agentsLabel = menuById.agentes?.label ?? 'Agentes'
  // Ficheros: lo proyecta el kernel solo a quien puede verlo; el front pinta.
  const ficheros = menuById.ficheros?.visible ? menuById.ficheros : null
  // GEN-PX-021: las entradas «Próx.» (Informes, Áreas…) no se pintan: solo lo que ya funciona.
  const otras = menu.filter(m => m.visible && m.enabled && !IDS_PROPIOS.includes(m.id) && !m.id.startsWith(PREFIJO_MECANISMO) && !m.id.startsWith(PREFIJO_EMPRESA))

  // Filtrar chats y agentes por búsqueda
  const searchLower = search.toLowerCase()
  const filteredChats = search
    ? chats.filter(c =>
        (c.titulo ?? '').toLowerCase().includes(searchLower) ||
        (c.de_que_va ?? '').toLowerCase().includes(searchLower)
      )
    : chats
  const filteredAgents = search
    ? agents.filter(a =>
        a.nombre.toLowerCase().includes(searchLower) ||
        a.rol.toLowerCase().includes(searchLower)
      )
    : agents

  function toggleSection(id) {
    setExpandedSection(s => (s === id ? null : id))
  }

  if (collapsed) return null

  const activos = agents.filter(a => a.puede_iniciar_chat !== false).length
  // Por departamento (data-driven). Se listan TODOS los agentes: los inactivos
  // quedan marcados como no disponibles (no se esconden).
  const agentGroups = groupByDepartment(filteredAgents)
  // Con búsqueda se abren todos los departamentos para ver las coincidencias.
  const estaAbierto = (grupo, i) => search ? true : (deptAbierto[grupo.departamento] ?? i === 0)
  const ringColorOf = a => agentColorOf(agentPalette ?? {}, a.id, resolvedTheme)

  // Pie de owner — nuevo (Gabinete): identidad del usuario de la sesión,
  // data-driven (badge = user.rol tal cual lo proyecta el descriptor;
  // nunca se decide en el front, R-PT-02).
  const pie = user && (
    <div className="left-rail__owner">
      <AgentAvatar name={user.display_name} size={30} />
      <span className="left-rail__owner-info">
        <span className="left-rail__owner-name">{user.display_name}</span>
        {user.rol && (
          <span className="agent-role-badge">{user.rol}</span>
        )}
      </span>
    </div>
  )

  if (seccion) return (
    <aside className="left-rail left-rail--contextual">
      <CarrilContextual
        titulo={seccion.padre.label}
        hijos={seccion.hijos}
        activoId={seccion.activo.id}
        onElegir={h => onElegirHijo?.(h)}
      />
      {pie}
    </aside>
  )

  return (
    <aside className="left-rail">
      {/* Búsqueda global */}
      <div className="left-rail__search">
        <input
          type="search"
          className="rail-search"
          placeholder={soloHistorico ? 'Buscar conversaciones…' : 'Buscar chats, agentes...'}
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Buscar"
        />
      </div>

      {/* GEN-PX-007: abierto desde Inicio, el panel es solo el histórico (cada cosa una
          sola vez): sin «Agentes en este chat», agentes ni ficheros. */}
      {!soloHistorico && (
      <>
      {/* 1 · Agentes en este chat (antes en el panel derecho) */}
      <AgentesEnEsteChat
        activeChat={activeChat}
        sources={sources}
        agentesQueRespondieron={agentesQueRespondieron}
        onAnadirAgente={onAnadirAgente}
      />

      {/* 2 · Agentes por departamento */}
      <div className="rail-section rail-mostrador">
        <div className="rail-section__header rail-section__header--static">
          <span className="rail-section__label"><Users {...NAV_ICON_PROPS} /> {agentsLabel}</span>
          <span className="rail-section__count">{activos} activos</span>
        </div>
        <div className="rail-section__body">
          {agentGroups.length === 0 && <p className="rail-empty">No hay agentes</p>}
          {agentGroups.map((grupo, i) => (
            <DepartamentoAgentes
              key={grupo.departamento}
              grupo={grupo}
              abierto={estaAbierto(grupo, i)}
              onToggle={() => setDeptAbierto(d => ({ ...d, [grupo.departamento]: !estaAbierto(grupo, i) }))}
              ringColorOf={ringColorOf}
              onSelectAgent={onSelectAgent}
            />
          ))}
        </div>
      </div>

      {/* 3 · Ficheros, y el resto de secciones de trabajo del descriptor
          (Informes, Áreas…: un ítem deshabilitado sale como «Próx.»). */}
      {[ficheros, ...otras].filter(Boolean).map(item => {
        const Ico = SECTION_ICONS[item.icon] ?? FALLBACK_ICON
        return (
          <div key={item.id} className="rail-section">
            <button
              className={
                'rail-section__header'
                + (!item.enabled ? ' rail-section__header--disabled' : '')
                + (item.enabled && activeView === item.id ? ' rail-section__header--active' : '')
              }
              onClick={() => item.enabled && onNavigate?.(item.id)}
              aria-current={activeView === item.id ? 'page' : undefined}
              disabled={!item.enabled}
              title={!item.enabled ? 'Próximamente' : undefined}
            >
              <span className="rail-section__label">
                <Ico {...NAV_ICON_PROPS} /> {item.label}
              </span>
              {!item.enabled && <span className="rail-section__badge">Próx.</span>}
            </button>
          </div>
        )
      })}
      </>
      )}

      {/* 4 · Nueva conversación */}
      <button className="new-chat-btn" onClick={onNewChat}>
        <span className="new-chat-btn__icon">+</span>
        Nueva conversación
      </button>

      {/* 5 · Conversaciones */}
      <div className="rail-section">
        <button
          className="rail-section__header"
          onClick={() => toggleSection('chats')}
          aria-expanded={soloHistorico || expandedSection === 'chats'}
        >
          <span className="rail-section__label"><MessageSquare {...NAV_ICON_PROPS} /> {chatsLabel}</span>
          <span className="rail-section__arrow">
            <Chevron open={expandedSection === 'chats'} />
          </span>
        </button>
        {(soloHistorico || expandedSection === 'chats') && (
          <div className="rail-section__body">
            {filteredChats.length === 0 && (
              <p className="rail-empty">Sin conversaciones</p>
            )}
            {filteredChats.map(c => (
              <ChatItem
                key={c.chat_id}
                chat={c}
                active={c.chat_id === activeChatId}
                onClick={() => onSelectChat(c.chat_id)}
                onDelete={onDeleteChat}
              />
            ))}
          </div>
        )}
      </div>

      {pie}
    </aside>
  )
}
